import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogContent, DialogHeader } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ArrowLeft, List, TrendingUp, Columns } from 'lucide-react';
import { Button } from './ui/button';
import type { WeldPass } from './PassViewTable';
import type { WeldSegmentRecord } from './WeldSegmentViewTable';
import WeldPassDetailTable from './WeldPassDetailTable';
import PassViewGraph from './ViewGraph';
import { useWeldSegmentDetail } from '../redux/WeldData/hooks/useWeldData';
import { formatDuration, formatDurationFromSeconds, formatDateTime, formatTimestamp } from '../lib/formatUtils';
import { convertWeldDataValue, getParameterUnitSuffix } from '../utils/unitUtils';
import { HEAT_INPUT_DISPLAY_NAME } from '../utils/weldParameterLabels';
import { ColumnSelectorModal, type ColumnDefinition } from './ui/ColumnSelectorModal';
import { createUpdateUserTableLayout, getAllUserTableLayouts } from '../redux/UserTableLayout/actions';
import { filterColumnsForManualWelding, filterColumnDefinitionsForManualWelding, formatManualWeldingBugTypeLabel } from '../utils/manualWeldingUtils';

interface WeldSegmentDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  passData: WeldPass;
  segmentData: WeldSegmentRecord;
  weldData?: any;
  statusCalculationMethod?: string;
}

const WELD_SEGMENT_DETAIL_COLUMNS: ColumnDefinition[] = [
  { key: 'sl-no', label: 'Sl.no', category: 'Basic Info' },
  // Status column temporarily disabled for weld segment detail — re-enable when needed
  // { key: 'status', label: 'Status', category: 'Basic Info' },
  { key: 'event', label: 'Event', category: 'Basic Info' },
  { key: 'time', label: 'Time', category: 'Basic Info' },
  { key: 'tilt', label: 'Tilt', category: 'Basic Info' },
  { key: 'pass', label: 'Pass', category: 'Basic Info' },
  { key: 'pipe', label: 'Pipe', category: 'Basic Info' },
  { key: 'zone', label: 'Zone', category: 'Basic Info' },
  { key: 'distance', label: 'Distance', category: 'Basic Info' },
  // Parameter order matches Segment View / Pass View (manual Ti log)
  { key: 'current', label: 'Current', category: 'Parameters' },
  { key: 'voltage', label: 'Voltage', category: 'Parameters' },
  { key: 'travel-speed', label: 'Travel Speed', category: 'Parameters' },
  { key: 'true-energy', label: 'True Energy', category: 'Parameters' },
  { key: 'heat', label: HEAT_INPUT_DISPLAY_NAME, category: 'Parameters' },
  { key: 'wire-speed', label: 'Wire Speed', category: 'Parameters' },
  { key: 'oscillation-width', label: 'Oscillation Width', category: 'Parameters' },
  { key: 'target', label: 'Target', category: 'Parameters' },
  { key: 'horizontal-bias', label: 'Horizontal Bias', category: 'Parameters' },
  { key: 'frequency', label: 'Frequency', category: 'Parameters' },
  { key: 'total-wire-consumed', label: 'Total Wire Consumed', category: 'Parameters' },
  { key: 'head-angle', label: 'Head Angle', category: 'Parameters' },
  { key: 'arc-impedance', label: 'Arc Impedance', category: 'Parameters' },
];

const WELD_SEGMENT_DETAIL_COLUMN_DEFINITIONS = filterColumnDefinitionsForManualWelding(WELD_SEGMENT_DETAIL_COLUMNS);

const TABLE_NAME = 'weldSegmentDetail';

const getAllColumnKeys = (columns: ColumnDefinition[]): string[] => {
  const keys: string[] = [];
  columns.forEach((col) => {
    keys.push(col.key);
    col.subColumns?.forEach((sub) => keys.push(sub.key));
  });
  return keys;
};

const getDefaultVisibleColumns = (): string[] =>
  filterColumnsForManualWelding(getAllColumnKeys(WELD_SEGMENT_DETAIL_COLUMNS));

const colorPalette = [
  'bg-green-200', 'bg-blue-100', 'bg-green-100', 'bg-red-100',
  'bg-cyan-100', 'bg-teal-100', 'bg-indigo-100', 'bg-lime-100',
  'bg-pink-100', 'bg-rose-100', 'bg-emerald-100', 'bg-violet-100',
  'bg-amber-100', 'bg-sky-100', 'bg-stone-100', 'bg-slate-100',
];

const WeldSegmentDetailModal: React.FC<WeldSegmentDetailModalProps> = ({
  isOpen,
  onClose,
  passData,
  segmentData,
  weldData,
  statusCalculationMethod = 'Instantaneous',
}) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('table');
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [showAllInfo, setShowAllInfo] = useState(false);
  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => getDefaultVisibleColumns());
  const [defaultColumnsFromAPI, setDefaultColumnsFromAPI] = useState<string[] | null>(null);
  const layoutIdRef = useRef<number | null>(null);

  const userTableLayouts = useSelector((state: any) => state.userTableLayout?.layouts || []);
  const createUpdateSuccess = useSelector((state: any) => state.userTableLayout?.currentLayout);

  const {
    weldSegmentDetail,
    weldSegmentDetailLoading,
    weldSegmentDetailError,
    loadWeldSegmentDetail,
    clearWeldSegmentDetailData,
  } = useWeldSegmentDetail();

  useEffect(() => {
    if (isOpen) {
      dispatch(getAllUserTableLayouts({ table_name: TABLE_NAME }));
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (userTableLayouts.length > 0) {
      const layout = userTableLayouts.find((item: any) => item.table_name === TABLE_NAME);
      if (layout) {
        layoutIdRef.current = layout.user_table_layout_id;
        if (layout.layout?.columns && Array.isArray(layout.layout.columns)) {
          const columns = filterColumnsForManualWelding(layout.layout.columns).filter(
            (key: string) => key !== 'status'
          );
          setVisibleColumns(columns);
          setDefaultColumnsFromAPI(columns);
        }
      }
    }
  }, [userTableLayouts]);

  useEffect(() => {
    if (createUpdateSuccess && createUpdateSuccess.table_name === TABLE_NAME) {
      layoutIdRef.current = createUpdateSuccess.user_table_layout_id;
    }
  }, [createUpdateSuccess]);

  const handleColumnsSave = useCallback((selectedColumns: string[]) => {
    const filteredColumns = filterColumnsForManualWelding(selectedColumns);
    setVisibleColumns(filteredColumns);
    const payload: any = {
      table_name: TABLE_NAME,
      layout: { columns: filteredColumns },
    };
    if (layoutIdRef.current) {
      payload.user_table_layout_id = layoutIdRef.current;
    }
    dispatch(createUpdateUserTableLayout(payload));
  }, [dispatch]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    // Always send both S and T keys. Empty T is valid for orphan segments;
    // only skip when both keys are missing.
    const welduidS =
      (segmentData.welduid_s_event_torch && String(segmentData.welduid_s_event_torch).trim()) ||
      (passData?.welduid_s_event_torch && String(passData.welduid_s_event_torch).trim()) ||
      '';
    const welduidT =
      segmentData.welduid_t_event_torch == null
        ? ''
        : String(segmentData.welduid_t_event_torch).trim();

    if (!welduidS && !welduidT) {
      return;
    }

    const torchType =
      (segmentData.torch && segmentData.torch !== '-'
        ? segmentData.torch
        : null) ||
      (passData.torch && passData.torch !== '-' ? passData.torch : null) ||
      'Lead';

    loadWeldSegmentDetail(welduidS, welduidT, 'Pass', torchType);

    return () => {
      clearWeldSegmentDetailData();
      setSelectedRowIndex(null);
    };
  }, [isOpen, passData, segmentData, loadWeldSegmentDetail, clearWeldSegmentDetailData]);

  const detailData = useMemo(
    () => weldSegmentDetail?.data?.data ?? [],
    [weldSegmentDetail]
  );

  const tableVisibleColumns = useMemo(
    () => filterColumnsForManualWelding(visibleColumns).filter((key) => key !== 'status'),
    [visibleColumns]
  );

  useEffect(() => {
    setSelectedRowIndex(null);
  }, [detailData]);

  const getUnitLabel = (parameter: string): string => getParameterUnitSuffix(parameter);

  const convertDiameterValue = (value: any): string => {
    if (value === undefined || value === null || value === '') return '-';
    const numericValue = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(numericValue)) return String(value);
    return convertWeldDataValue(numericValue).toFixed(2);
  };

  const details = useMemo(() => [
    { label: 'Job Number', value: passData.jobNumber || weldData?.wpsNumber || segmentData.job_number || '-' },
    { label: 'Pass', value: segmentData.pass_name || passData.name || '-' },
    { label: 'Station', value: segmentData.station_number ?? passData.stationNumber ?? '-' },
    { label: 'Bug Type', value: formatManualWeldingBugTypeLabel(segmentData.bug_type || passData.bugType) },
    { label: 'Torch', value: segmentData.torch || passData.torch || '-' },
    { label: 'S Event', value: segmentData.s_event || '-' },
    { label: 'T Event', value: segmentData.t_event || '-' },
    { label: 'Segment Time', value: segmentData.s_time != null ? formatTimestamp(segmentData.s_time) : '-' },
    { label: 'Arc Time', value: segmentData.arc_time != null ? formatDurationFromSeconds(segmentData.arc_time) : '-' },
    { label: 'Segment Status', value: segmentData.segment_avg_pass_status || '-' },
    { label: `Pipe Diameter (${getUnitLabel('pipe_diameter')})`, value: convertDiameterValue(passData.pipeDiameter) },
    { label: 'Weld Start Time', value: passData.startTime ? formatDateTime(passData.startTime) : '-' },
    { label: 'Weld End Time', value: passData.endTime ? formatDateTime(passData.endTime) : '-' },
    { label: 'Weld Time', value: passData.duration != null ? formatDuration(passData.duration) : '-' },
    { label: 'Ti Records', value: String(detailData.length) },
  ], [passData, segmentData, weldData, detailData.length, getUnitLabel]);

  const infoItems = details.map((item, index) => ({
    ...item,
    bgColor: colorPalette[index % colorPalette.length],
  }));

  const visibleItems = showAllInfo ? infoItems : infoItems.slice(0, 7);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[100vw] w-screen h-screen max-h-screen p-0 gap-0 overflow-hidden">
        <div className="flex flex-col h-full font-sans overflow-hidden">
          <DialogHeader className="px-6 py-4 border-b flex flex-row items-center justify-between bg-white flex-shrink-0">
            <div className="flex items-center gap-4">
              <Button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" variant="outline">
                <ArrowLeft className="h-5 w-5 text-gray-500" />
              </Button>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl font-bold">
                  {weldData?.weldNumber || passData.bugNumber || passData.id || '-'} — Segment Detail
                </h1>
                <div className="text-xs sm:text-sm text-gray-600 mt-1 space-y-1">
                  <div className="flex flex-wrap gap-2">
                    {visibleItems.map((item, index) => (
                      <span key={index} className={`${item.bgColor} px-2 py-1 rounded text-xs`}>
                        {item.label}: {item.value}
                      </span>
                    ))}
                    {infoItems.length > 7 && (
                      <button
                        onClick={() => setShowAllInfo(!showAllInfo)}
                        className="bg-gray-200 hover:bg-gray-300 px-3 py-1 rounded text-xs font-medium transition-colors duration-200 flex items-center gap-1"
                      >
                        {showAllInfo ? 'Show Less' : `Show More (+${infoItems.length - 7})`}
                        <span className="text-xs">{showAllInfo ? '▲' : '▼'}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 flex flex-col min-h-0">
            {weldSegmentDetailLoading ? (
              <div className="flex-1 flex items-center justify-center bg-white">
                <div className="flex flex-col items-center space-y-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#EB1C37]" />
                  <p className="text-gray-600 text-lg font-medium">Loading segment detail data...</p>
                  <p className="text-gray-400 text-sm">Please wait while we fetch the data</p>
                </div>
              </div>
            ) : weldSegmentDetailError ? (
              <div className="flex-1 flex items-center justify-center bg-white">
                <div className="flex flex-col items-center space-y-4 max-w-md text-center">
                  <div className="text-red-500 text-5xl">⚠️</div>
                  <p className="text-red-600 text-lg font-medium">Error loading segment detail data</p>
                  <p className="text-gray-500 text-sm">{weldSegmentDetailError}</p>
                  <Button onClick={onClose} variant="outline">Close</Button>
                </div>
              </div>
            ) : (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full h-full flex flex-col">
                <div className="sticky top-0 bg-white rounded-t-lg z-10 border-b border-gray-200 flex-shrink-0">
                  <div className="flex items-center justify-between px-1 md:px-4 py-2">
                    <TabsList className="h-auto flex-1 justify-start gap-2 md:gap-4 lg:gap-8 border-b bg-transparent py-2 flex-wrap">
                      <TabsTrigger value="table" className="data-[state=active]:border-[#EB1C37] data-[state=active]:text-[#EB1C37] relative px-1 pb-3 pt-2 text-sm md:text-base font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-[0.5px] whitespace-nowrap">
                        <List className="inline-block mr-1 h-4 w-4 align-text-bottom" /> Data Analysis
                      </TabsTrigger>
                      <TabsTrigger value="graph" className="data-[state=active]:border-[#EB1C37] data-[state=active]:text-[#EB1C37] relative px-1 pb-3 pt-2 text-sm md:text-base font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-[0.5px] whitespace-nowrap">
                        <TrendingUp className="inline-block mr-1 h-4 w-4 align-text-bottom" /> Graph View
                      </TabsTrigger>
                    </TabsList>
                    {activeTab === 'table' && (
                      <div className="flex gap-2 px-2">
                        <Button onClick={() => setIsColumnModalOpen(true)} className="flex items-center gap-2" title="Configure columns">
                          <Columns className="h-4 w-4" />
                          <span className="hidden sm:inline">Columns</span>
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                <TabsContent value="table" className="flex-1 min-h-0 overflow-hidden">
                  <div className="h-full px-4">
                    <WeldPassDetailTable
                      detailData={detailData}
                      onRowSelect={setSelectedRowIndex}
                      selectedRowIndex={selectedRowIndex}
                      loading={weldSegmentDetailLoading}
                      statusCalculationMethod={statusCalculationMethod}
                      visibleColumns={tableVisibleColumns}
                      useManualSegmentParameterOrder
                    />
                  </div>
                </TabsContent>
                <TabsContent value="graph" className="flex-1 min-h-0 overflow-hidden">
                  <div className="h-full overflow-y-auto">
                    <div className="p-4 pb-20">
                      <PassViewGraph
                        detailData={detailData}
                        projectNumber={passData.bugNumber || ''}
                        projectName={passData.name || ''}
                      />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </div>
        </div>
      </DialogContent>

      <ColumnSelectorModal
        isOpen={isColumnModalOpen}
        onClose={() => setIsColumnModalOpen(false)}
        onSave={handleColumnsSave}
        columns={WELD_SEGMENT_DETAIL_COLUMN_DEFINITIONS}
        selectedColumns={visibleColumns}
        title="Select Columns - Weld Segment Detail"
        defaultColumns={defaultColumnsFromAPI || getDefaultVisibleColumns()}
      />
    </Dialog>
  );
};

export default WeldSegmentDetailModal;
